import { LinkButton } from '@euler/app/components/LinkButton';
import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { QuotationSubjectConstructionItemView } from '@euler/app/flows/task/pages/quotation/components/quotation-form/ConstructionItemView';
import { useQuotationFormContext } from '@euler/app/flows/task/pages/quotation/components/quotation-form/Context';
import { SubjectIssueList } from '@euler/app/flows/task/pages/quotation/components/quotation-form/SubjectIssueList';
import { QuotationFormSubjectPanelHeader } from '@euler/app/flows/task/pages/quotation/components/quotation-form/SubjectPanelHeader';
import { QuotationSubjectSubTotalInfo } from '@euler/app/flows/task/pages/quotation/components/quotation-form/SubjectSubTotalInfo';
import { useQuotationContext } from '@euler/app/flows/task/pages/quotation/components/QuotationContext';
import { keyOfIssue } from '@euler/app/flows/task/pages/quotation/functions/utils';
import { QuotationIssueInfo } from '@euler/app/flows/task/pages/quotation/types/QuotationCatalog';
import { Collapsible } from '@euler/components/collapsible';
import { Icon } from '@euler/components/Icomoon';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import {
  QuotationConstructionItemStaged,
  QuotationIssuePayload,
  QuotationSubjectStaged,
} from '@euler/model/task-detail';
import { reconciliateEntities } from '@euler/utils/reconciliateEntities';
import produce from 'immer';
import { nanoid } from 'nanoid/non-secure';
import { memo, Suspense, useCallback, useMemo } from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import invariant from 'tiny-invariant';

type Props = {
  editable?: boolean;
  index: number;
  subject: QuotationSubjectStaged;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  style?: StyleProp<ViewStyle>;
  onMoveUp?: (subject: QuotationSubjectStaged, index: number) => void;
  onMoveDown?: (subject: QuotationSubjectStaged, index: number) => void;
  onRemove?: (subject: QuotationSubjectStaged, index: number) => void;
  onChange?: (subject: QuotationSubjectStaged, index: number) => void;
  onCheckNamePermitted: (
    subject: QuotationSubjectStaged,
    index: number,
    name: string,
  ) => boolean;
};

type IssueSyncResult = {
  newIssues: QuotationIssueInfo[];
  removedIssues: QuotationIssueInfo[];
  updatedIssues: Partial<QuotationIssueInfo>[];
  hasChanges: boolean;
};

export const WiredQuotationFormSubjectPanel = memo(
  ({ subject, index, onChange, ...props }: Props) => {
    const { catalog } = useQuotationContext();

    const issueSyncResult = useMemo(() => {
      const syncResult: IssueSyncResult = {
        newIssues: [],
        removedIssues: [],
        updatedIssues: [],
        hasChanges: false,
      };
      const subjectInfo = catalog.subjectMap.get(subject.name);
      if (subjectInfo == null) return syncResult;
      const res = reconciliateEntities<
        QuotationIssueInfo | QuotationIssuePayload
      >(subject.issues, subjectInfo.issues, {
        getKey: x => keyOfIssue(x),
        diff: (a, b) => {
          const [u, v] = [
            a.medias
              .map(x => x.cid)
              .sort()
              .join(','),
            b.medias
              .map(x => x.cid)
              .sort()
              .join(','),
          ];
          return u !== v
            ? {
                medias: b.medias,
              }
            : {};
        },
      });
      syncResult.newIssues = res.entitiesToCreate as QuotationIssueInfo[];
      syncResult.removedIssues = res.entitiesToDelete as QuotationIssueInfo[];
      syncResult.updatedIssues =
        res.entitiesToUpdate as Partial<QuotationIssueInfo>[];
      syncResult.hasChanges =
        res.entitiesToCreate.length > 0 ||
        res.entitiesToDelete.length > 0 ||
        res.entitiesToUpdate.length > 0;
      return syncResult;
    }, [catalog.subjectMap, subject.issues, subject.name]);

    const onSyncIssues = useCallback(() => {
      const subjectInfo = catalog.subjectMap.get(subject.name);
      const updated = produce(subject, draft => {
        draft.issues =
          subjectInfo?.issues.map<QuotationIssuePayload>(issue => ({
            ...issue,
            key: keyOfIssue(issue),
            medias: issue.medias.map(media => ({
              ...media,
              id: undefined,
            })),
          })) ?? [];
      });
      onChange?.(updated, index);
    }, [catalog.subjectMap, index, onChange, subject]);

    return (
      <Suspense fallback={null}>
        <QuotationFormSubjectPanel
          subject={subject}
          index={index}
          hasIssuesChanges={issueSyncResult.hasChanges}
          onChange={onChange}
          onSyncIssues={onSyncIssues}
          {...props}
        />
      </Suspense>
    );
  },
);

export const QuotationFormSubjectPanel = memo(
  ({
    editable,
    index,
    subject,
    style,
    hasIssuesChanges,
    canMoveUp,
    canMoveDown,
    onMoveUp,
    onMoveDown,
    onRemove,
    onChange,
    onCheckNamePermitted,
    onSyncIssues,
  }: Props & {
    hasIssuesChanges?: boolean;
    onSyncIssues: () => void;
  }) => {
    const { rpx } = useResponsiveContext();

    const { subjectWhoseIssuesBeingEdited$ } = useQuotationFormContext();

    const confirm = useConfirm();

    const onEditIssues = useCallback(() => {
      subjectWhoseIssuesBeingEdited$.next(subject);
    }, [subject, subjectWhoseIssuesBeingEdited$]);

    const onMoveUpPress = useCallback(() => {
      onMoveUp?.(subject, index);
    }, [index, onMoveUp, subject]);

    const onMoveDownPress = useCallback(() => {
      onMoveDown?.(subject, index);
    }, [index, onMoveDown, subject]);

    const onRemovePress = useCallback(async () => {
      if (await confirm({ title: '确定删除该报价项目？' })) {
        onRemove?.(subject, index);
      }
    }, [index, onRemove, subject, confirm]);

    const onNameChange = useCallback(
      (name: string) => {
        const updated = produce(subject, draft => {
          draft.name = name;
        });
        onChange?.(updated, index);
      },
      [index, onChange, subject],
    );

    const onCheckChange = useCallback(
      (checked: boolean) => {
        const updated = produce(subject, draft => {
          draft.included = checked;
        });
        onChange?.(updated, index);
      },
      [index, onChange, subject],
    );

    const onContentChange = useCallback(
      (updated: QuotationSubjectStaged) => {
        onChange?.(updated, index);
      },
      [index, onChange],
    );

    const isNamePermitted = useCallback(
      (name: string) => {
        return onCheckNamePermitted(subject, index, name);
      },
      [index, onCheckNamePermitted, subject],
    );

    return (
      <Collapsible.Panel
        style={style}
        headerContainerStyle={{ alignItems: 'center' }}
        headerStyle={{ paddingRight: rpx(10) }}
        caretColor="#777"
        header={
          <QuotationFormSubjectPanelHeader
            index={index}
            title={subject.name}
            editable={editable}
            checked={subject.included}
            isNamePermitted={isNamePermitted}
            onEditIssuesPress={onEditIssues}
            onCheckChange={onCheckChange}
            canMoveUp={canMoveUp}
            canMoveDown={canMoveDown}
            onMoveUpPress={onMoveUpPress}
            onMoveDownPress={onMoveDownPress}
            onRemovePress={onRemovePress}
            onNameChange={onNameChange}
          />
        }
        css={`
          background-color: #fff;
          padding-top: ${rpx(10)}px;
          padding-bottom: ${rpx(10)}px;
        `}
      >
        <Content
          subject={subject}
          editable={editable}
          hasIssuesChanges={hasIssuesChanges}
          onChange={onContentChange}
          onSyncIssues={onSyncIssues}
        />
      </Collapsible.Panel>
    );
  },
);

const Content = memo(
  ({
    editable,
    subject,
    hasIssuesChanges,
    onChange,
    onSyncIssues,
  }: {
    editable?: boolean;
    subject: QuotationSubjectStaged;
    hasIssuesChanges?: boolean;
    onChange?: (subject: QuotationSubjectStaged) => void;
    onSyncIssues: () => void;
  }) => {
    const { rpx } = useResponsiveContext();

    const items = useMemo(
      () => (editable ? subject.items : subject.items.filter(x => x.included)),
      [editable, subject.items],
    );

    const { constructionItemBeingEdited$, materialBeingEdited$ } =
      useQuotationFormContext();

    const onItemChange = useCallback(
      (item: QuotationConstructionItemStaged) => {
        const updated = produce(subject, draft => {
          const index = draft.items.findIndex(x => x.name === item.name);
          if (index >= 0) {
            draft.items[index] = item;
          }
          if (draft.items.some(x => x.included) && !draft.included) {
            draft.included = true;
          }
          const includedItems = draft.items.filter(x => x.included);
          const includedMaterials = includedItems.flatMap(x =>
            x.materials.filter(t => t.included),
          );
          if (!includedMaterials.length) {
            draft.additionalMaterialSubTotalDiscount = undefined;
            draft.additionalMaterialSubTotalDiscountRemark = undefined;
          }
          if (!includedItems.length) {
            draft.additionalManHourCostSubTotalDiscount = undefined;
            draft.additionalManHourCostSubTotalDiscountRemark = undefined;
            draft.additionalSubTotalDiscount = undefined;
            draft.additionalMaterialSubTotalDiscountRemark = undefined;
          }
        });
        onChange?.(updated);
      },
      [onChange, subject],
    );

    const onRemoveConstructionItem = useCallback(
      (item: QuotationConstructionItemStaged) => {
        const updated = produce(subject, draft => {
          const index = draft.items.findIndex(x => x.name === item.name);
          if (index >= 0) {
            draft.items.splice(index, 1);
          }
          if (!draft.items.some(x => x.included)) {
            draft.included = false;
          }
        });
        onChange?.(updated);
      },
      [onChange, subject],
    );

    const onAddConstructionItem = useCallback(() => {
      constructionItemBeingEdited$.next({
        key: nanoid(),
        type: 'new',
        subject,
      });
    }, [constructionItemBeingEdited$, subject]);

    const onEditConstructionItem = useCallback(
      (item: QuotationConstructionItemStaged) => {
        constructionItemBeingEdited$.next({
          key: nanoid(),
          type: 'edit',
          subject,
          item,
          index: items.findIndex(x => x.name === item.name),
        });
      },
      [constructionItemBeingEdited$, items, subject],
    );

    const onAddMaterial = useCallback(
      (item: QuotationConstructionItemStaged) => {
        materialBeingEdited$.next({
          key: nanoid(),
          type: 'new',
          subject,
          item,
        });
      },
      [materialBeingEdited$, subject],
    );

    const onEditMaterial = useCallback(
      (item: QuotationConstructionItemStaged, index: number) => {
        const material = item.materials[index];
        invariant(material != null);
        materialBeingEdited$.next({
          key: nanoid(),
          type: 'edit',
          subject,
          item,
          material,
          index,
        });
      },
      [materialBeingEdited$, subject],
    );

    const onManHourCostDiscountChange = useCallback(
      (discount: number) => {
        const updated = produce(subject, draft => {
          draft.additionalManHourCostSubTotalDiscount = discount;
        });
        onChange?.(updated);
      },
      [onChange, subject],
    );

    const onMaterialDiscountChange = useCallback(
      (discount: number) => {
        const updated = produce(subject, draft => {
          draft.additionalMaterialSubTotalDiscount = discount;
        });
        onChange?.(updated);
      },
      [onChange, subject],
    );

    const onSubTotalDiscountChange = useCallback(
      (discount: number) => {
        const updated = produce(subject, draft => {
          draft.additionalSubTotalDiscount = discount;
        });
        onChange?.(updated);
      },
      [onChange, subject],
    );

    return (
      <>
        {hasIssuesChanges && (
          <StackLayout
            direction="row"
            alignItems="center"
            gap={rpx(3)}
            css={`
              margin-top: ${rpx(8)}px;
              background-color: rgba(241, 182, 157, 0.592);
              padding: ${rpx(3)}px ${rpx(12)}px;
              border-radius: ${rpx(20)}px;
            `}
          >
            <Icon
              name="information-solid"
              size={rpx(14)}
              color="#000"
              css={`
                top: ${rpx(0.5)}px;
              `}
            />
            <Typography.Text
              size={rpx(13)}
              color="#000"
              letterSpacing={rpx(-0.2)}
            >
              当前报价项目故障信息与检测结果不一致,
            </Typography.Text>
            <LinkButton
              text="请点击同步故障信息"
              textStyle={{ fontSize: rpx(13), letterSpacing: rpx(-0.2) }}
              onPress={onSyncIssues}
            />
          </StackLayout>
        )}
        <SubjectIssueList
          issues={subject.issues}
          css={`
            margin-top: ${rpx(8)}px;
          `}
        />
        <StackLayout
          gap={rpx(15)}
          css={`
            margin-top: ${rpx(8)}px;
          `}
        >
          {items.map(item => (
            <QuotationSubjectConstructionItemView
              item={item}
              key={item.name}
              editable={editable}
              onChange={onItemChange}
              onEdit={onEditConstructionItem}
              onRemove={onRemoveConstructionItem}
              onAddMaterial={onAddMaterial}
              onEditMaterial={onEditMaterial}
            />
          ))}
          {items.length === 0 && <EmptyConstructionItems />}
          <QuotationSubjectSubTotalInfo
            subject={subject}
            editable={editable}
            onAddConstructionItem={onAddConstructionItem}
            onManHourCostDiscountChange={onManHourCostDiscountChange}
            onMaterialDiscountChange={onMaterialDiscountChange}
            onSubTotalDiscountChange={onSubTotalDiscountChange}
            css={`
              border-top-width: 1px;
              border-top-color: #ddd;
              padding-top: ${rpx(8)}px;
            `}
          />
        </StackLayout>
      </>
    );
  },
);

const EmptyConstructionItems = memo(() => {
  const { rpx } = useResponsiveContext();
  return (
    <StackLayout
      direction="row"
      alignItems="center"
      gap={rpx(3)}
      css={`
        padding: ${rpx(5)}px 0;
      `}
    >
      <Icon name="information-outline" size={rpx(14)} color="#aaa" />
      <Typography.Text size={rpx(14)} color="#aaa">
        尚未添加相关施工项目
      </Typography.Text>
    </StackLayout>
  );
});
